import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import db from '../../../firebase';
import {
  Card,
  CardBody,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Spinner,
  Input,
  Tooltip,
} from '@material-tailwind/react';
import {
  PhotoIcon,
  MapPinIcon,
  ClockIcon,
  XMarkIcon,
  PlusIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  CameraIcon,
  LinkIcon,
  CheckIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatHours = (value) => {
  if (!value && value !== 0) return 'N/A';
  return `${value.toLocaleString()} hrs`;
};

// Inline SVG data URI — renders as a real image in HTML previews.
// Uses width/height="100%" so it fills whatever container it's in,
// and preserveAspectRatio="xMidYMid slice" to cover like a photo would.
const AIRPLANE_PLACEHOLDER = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 300">' +
  '<rect width="400" height="300" fill="#e8ebf0"/>' +
  '<g transform="translate(200,150) scale(0.9)" opacity="0.45">' +
  '<path d="M0-40 C6-40 10-36 10-30 L10-8 L45 12 C48 13.5 48 18 45 19 L10 10 L10 25 L20 32 C22 33.5 22 36 20 37 L0 32 L-20 37 C-22 36 -22 33.5 -20 32 L-10 25 L-10 10 L-45 19 C-48 18 -48 13.5 -45 12 L-10-8 L-10-30 C-10-36 -6-40 0-40Z" fill="#6b7280"/>' +
  '</g>' +
  '</svg>'
);

function isPlaceholder(url) {
  return url && url.startsWith('data:image/svg+xml,');
}

function ImagePicker({ photoUrl, photoUrls = [], onSelect }) {
  const [open, setOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Build deduplicated list of available image URLs (include current photoUrl if not in photoUrls)
  const availablePhotos = useMemo(() => {
    const urls = [...photoUrls];
    if (photoUrl && !isPlaceholder(photoUrl) && !urls.includes(photoUrl)) {
      urls.unshift(photoUrl);
    }
    return urls;
  }, [photoUrl, photoUrls]);

  const handleSelect = (url) => {
    if (url && url !== photoUrl) onSelect(url);
    setOpen(false);
  };

  const handleCustomSubmit = () => {
    const url = customUrl.trim();
    if (url) {
      onSelect(url);
      setCustomUrl('');
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="absolute inset-0 z-[5]">
      {/* Clickable overlay to open picker */}
      <button
        type="button"
        className="absolute inset-0 w-full h-full cursor-pointer bg-transparent group/picker"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        title="Change image"
      >
        <span className="absolute bottom-1 left-1 bg-black/60 text-white rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1 opacity-0 group-hover/picker:opacity-100 transition-opacity">
          <CameraIcon className="h-3 w-3" />
          {availablePhotos.length > 1 ? `${availablePhotos.length} photos` : 'Change'}
        </span>
      </button>

      {/* Picker dropdown */}
      {open && (
        <div
          className="absolute top-full left-0 right-0 bg-white border border-gray-200 shadow-lg rounded-b-lg z-20 p-2 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Thumbnail grid */}
          {availablePhotos.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto">
              {availablePhotos.map((url, i) => (
                <button
                  key={i}
                  type="button"
                  className={`relative flex-shrink-0 w-16 h-12 rounded border-2 overflow-hidden ${
                    url === photoUrl ? 'border-blue-500' : 'border-gray-200 hover:border-gray-400'
                  }`}
                  onClick={() => handleSelect(url)}
                >
                  <img src={url} alt={`Option ${i + 1}`} className="w-full h-full object-cover" />
                  {url === photoUrl && (
                    <span className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                      <CheckIcon className="h-4 w-4 text-white drop-shadow" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Use airplane placeholder option */}
          <button
            type="button"
            className={`flex items-center gap-1.5 w-full text-xs px-2 py-1.5 rounded border ${
              isPlaceholder(photoUrl) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-400 text-gray-600'
            }`}
            onClick={() => handleSelect(AIRPLANE_PLACEHOLDER)}
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            Use airplane icon
          </button>

          {/* Custom URL input */}
          <div className="flex gap-1">
            <div className="flex-1 relative">
              <input
                type="url"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                placeholder="Paste image URL..."
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 pl-6 focus:outline-none focus:border-blue-400"
              />
              <LinkIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={handleCustomSubmit}
              disabled={!customUrl.trim()}
              className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NoPriceCompCard({ comp, onPhotoChange }) {
  const {
    reg,
    registration,
    year,
    model,
    totalTime,
    total_time,
    source,
    photoUrl,
    location,
  } = comp;

  const displayReg = reg || registration;
  const displayHours = totalTime || total_time;
  const showPicker = onPhotoChange && (comp.photoUrls?.length > 1 || true); // always show for custom URL

  return (
    <Card className="overflow-hidden border-2 border-dashed border-gray-300">
      <div className="relative h-20 bg-gray-100 flex items-center justify-center">
        {isPlaceholder(photoUrl) ? (
          <PaperAirplaneIcon className="h-8 w-8 text-gray-400" />
        ) : photoUrl ? (
          <img
            src={photoUrl}
            alt={`${displayReg} ${model}`}
            className="w-full h-full object-cover opacity-75"
          />
        ) : (
          <PhotoIcon className="h-6 w-6 text-gray-300" />
        )}
        {source && (
          <Chip
            size="sm"
            value={source}
            className="absolute top-1 right-1 text-xs"
            color="blue-gray"
          />
        )}
        {showPicker && (
          <ImagePicker
            photoUrl={photoUrl}
            photoUrls={comp.photoUrls || []}
            onSelect={(url) => onPhotoChange(displayReg, url)}
          />
        )}
      </div>
      <CardBody className="p-2">
        <div className="flex items-start justify-between mb-1">
          <div>
            {(comp.id || comp.listingId) ? (
              <a
                href={`/dashboard/listing-details/${comp.id || comp.listingId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                {displayReg || 'N/A'}
              </a>
            ) : (
              <Typography variant="small" color="blue-gray" className="font-bold">
                {displayReg || 'N/A'}
              </Typography>
            )}
            <Typography variant="small" color="gray" className="text-xs">
              {year} {model}
            </Typography>
          </div>
          <Typography variant="small" className="italic text-gray-400 text-xs">
            Price on Request
          </Typography>
        </div>

        <div className="flex flex-wrap gap-1 text-xs">
          {displayHours && (
            <div className="flex items-center gap-1 text-gray-600">
              <ClockIcon className="h-3 w-3" />
              <span>{formatHours(displayHours)}</span>
            </div>
          )}
          {location && (
            <div className="flex items-center gap-1 text-gray-600">
              <MapPinIcon className="h-3 w-3" />
              <span>{location}</span>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function CompCard({ comp, onRemove, isRemoving, isExcluded, isSubject, onPhotoChange }) {
  const {
    reg,
    registration,
    year,
    model,
    totalTime,
    total_time,
    price_usd,
    source,
    photoUrl,
    location,
    relevanceNotes,
    daysOnMarket,
  } = comp;

  const displayReg = reg || registration;
  const displayHours = totalTime || total_time;
  const displayPrice = price_usd;

  return (
    <Card className={`overflow-hidden relative group ${isExcluded ? 'opacity-50 grayscale' : ''} ${isSubject ? 'ring-2 ring-blue-400' : ''}`}>
      {/* Toggle exclusion button */}
      {onRemove && (
        <IconButton
          size="sm"
          color={isExcluded ? "green" : "red"}
          variant="filled"
          className="absolute top-1 left-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 min-w-0"
          onClick={() => onRemove(displayReg)}
          disabled={isRemoving}
        >
          {isExcluded ? <ArrowPathIcon className="h-4 w-4" /> : <XMarkIcon className="h-4 w-4" />}
        </IconButton>
      )}

      <div className="relative h-28 bg-gray-100 flex items-center justify-center">
        {isPlaceholder(photoUrl) ? (
          <PaperAirplaneIcon className="h-10 w-10 text-gray-400" />
        ) : photoUrl ? (
          <img
            src={photoUrl}
            alt={`${displayReg} ${model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <PhotoIcon className="h-8 w-8 text-gray-300" />
        )}
        {source && (
          <Chip
            size="sm"
            value={source}
            className="absolute top-1 right-1 text-xs"
            color="blue-gray"
          />
        )}
        {onPhotoChange && (
          <ImagePicker
            photoUrl={photoUrl}
            photoUrls={comp.photoUrls || []}
            onSelect={(url) => onPhotoChange(displayReg, url)}
          />
        )}
      </div>
      <CardBody className="p-2">
        <div className="mb-1">
          <div className="flex items-start justify-between">
            <div>
              {(comp.id || comp.listingId) ? (
                <a
                  href={`/dashboard/listing-details/${comp.id || comp.listingId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {displayReg || 'N/A'}
                </a>
              ) : (
                <Typography variant="small" color="blue-gray" className="font-bold">
                  {displayReg || 'N/A'}
                </Typography>
              )}
              <Typography variant="small" color="gray" className="text-xs">
                {year} {model}
              </Typography>
            </div>
            {relevanceNotes && relevanceNotes.length > 0 && (
              <Tooltip
                content={
                  <div className="p-1">
                    <Typography variant="small" className="font-medium text-white text-xs mb-1">
                      Relevance
                    </Typography>
                    <ul className="list-disc list-inside text-xs">
                      {relevanceNotes.map((note, idx) => (
                        <li key={idx}>{note}</li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-blue-500 cursor-help" />
              </Tooltip>
            )}
          </div>
          <Typography variant="small" color="blue" className="font-bold mt-1">
            {formatPrice(displayPrice)}
          </Typography>
        </div>

        <div className="flex flex-wrap gap-1 text-xs">
          <div className="flex items-center gap-1 text-gray-600">
            <ClockIcon className="h-3 w-3" />
            <span>{formatHours(displayHours)}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1 text-gray-600">
              <MapPinIcon className="h-3 w-3" />
              <span>{location}</span>
            </div>
          )}
          {(daysOnMarket ?? comp.daysActive) !== undefined && (
            <Chip
              size="sm"
              value={`${daysOnMarket ?? comp.daysActive} DOM`}
              variant="ghost"
              color="gray"
              className="text-xs py-0"
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const EMPTY_COMP_FORM = {
  reg: '',
  year: '',
  totalTime: '',
  price_usd: '',
  source: '',
  model: '',
};

function OnMarketCompsSection({
  content,
  originalContent,
  onCompsChange,
  onPhotoChange,
  onResetToAI,
  excludedListings = [],
  onToggleExclusion,
  isUpdating,
  hasLocalChanges,
  subjectRegistration,
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCompForm, setNewCompForm] = useState(EMPTY_COMP_FORM);
  const [formError, setFormError] = useState('');

  // Enriched photoUrls fetched from Firestore for comps missing them
  const [enrichedPhotoUrls, setEnrichedPhotoUrls] = useState({});

  // Get current comps (may be AI-selected or user-edited)
  const rawComps = useMemo(() => content?.comparables || content?.listings || [], [content?.comparables, content?.listings]);

  // Merge enriched photoUrls into comps
  const currentComps = useMemo(() => rawComps.map(c => {
    const id = c.id || c.listingId;
    if (id && enrichedPhotoUrls[id] && (!c.photoUrls || c.photoUrls.length <= 1)) {
      return { ...c, photoUrls: enrichedPhotoUrls[id] };
    }
    return c;
  }), [rawComps, enrichedPhotoUrls]);

  // IDs that need image fetching — stable string so the effect only fires when this set changes
  const idsNeedingFetch = useMemo(() => {
    return rawComps
      .filter(c => {
        const id = c.id || c.listingId;
        return id && (!c.photoUrls || c.photoUrls.length <= 1) && !enrichedPhotoUrls[id];
      })
      .map(c => c.id || c.listingId)
      .sort()
      .join(',');
  }, [rawComps, enrichedPhotoUrls]);

  // Fetch images from Firestore for comps missing photoUrls
  useEffect(() => {
    if (!idsNeedingFetch) return;
    const ids = idsNeedingFetch.split(',');

    let cancelled = false;
    (async () => {
      const results = {};
      for (const id of ids) {
        try {
          const imgDoc = await getDoc(doc(db, `listing_aggregations_v1/${id}/listing_images/img`));
          if (imgDoc.exists()) {
            const urls = imgDoc.data()?.urls;
            if (Array.isArray(urls) && urls.length > 0) {
              // Deduplicate by storage path (strip query params), take most recent per unique image
              const byPath = {};
              for (const img of urls) {
                if (!img.link) continue;
                const key = img.link.split('?')[0];
                const ts = img.dt?._seconds || img.dt?.seconds || 0;
                if (!byPath[key] || ts > byPath[key].ts) {
                  byPath[key] = { link: img.link, ts };
                }
              }
              const dedupedLinks = Object.values(byPath).map(v => v.link);
              if (dedupedLinks.length > 0) {
                results[id] = dedupedLinks.slice(0, 6);
              }
            }
          }
        } catch (err) {
          console.warn(`Failed to fetch images for ${id}:`, err);
        }
      }
      if (!cancelled && Object.keys(results).length > 0) {
        setEnrichedPhotoUrls(prev => ({ ...prev, ...results }));
      }
    })();
    return () => { cancelled = true; };
  }, [idsNeedingFetch]);

  // Get AI-selected comps for comparison
  const aiSelectedComps = useMemo(() => originalContent?.comparables || originalContent?.listings || [], [originalContent?.comparables, originalContent?.listings]);

  // Check if user has modified the comps
  const hasUserEdits = useMemo(() => {
    if (!currentComps.length && !aiSelectedComps.length) return false;
    if (currentComps.length !== aiSelectedComps.length) return true;

    const currentRegs = currentComps.map(c => c.reg || c.registration).sort();
    const aiRegs = aiSelectedComps.map(c => c.reg || c.registration).sort();
    return JSON.stringify(currentRegs) !== JSON.stringify(aiRegs);
  }, [currentComps, aiSelectedComps]);

  const isExcluded = useCallback((reg) => {
    if (!reg) return false;
    return excludedListings.some(r => r.toUpperCase() === reg.toUpperCase());
  }, [excludedListings]);

  const handleRemove = (regToRemove) => {
    if (onToggleExclusion) {
      onToggleExclusion(regToRemove);
      return;
    }
    if (!onCompsChange) return;
    const updated = currentComps.filter(c => (c.reg || c.registration) !== regToRemove);
    onCompsChange(updated);
  };

  const handleAddComp = () => {
    if (!onCompsChange) return;

    // Validate required fields
    if (!newCompForm.reg.trim()) {
      setFormError('Registration is required');
      return;
    }
    if (!newCompForm.year) {
      setFormError('Year is required');
      return;
    }
    if (!newCompForm.price_usd) {
      setFormError('Price is required');
      return;
    }

    // Check for duplicate registration
    const existingReg = currentComps.find(
      c => (c.reg || c.registration)?.toUpperCase() === newCompForm.reg.toUpperCase()
    );
    if (existingReg) {
      setFormError('A comp with this registration already exists');
      return;
    }

    // Create new comp object
    const newComp = {
      reg: newCompForm.reg.toUpperCase(),
      year: parseInt(newCompForm.year, 10),
      price_usd: parseInt(newCompForm.price_usd, 10),
      totalTime: newCompForm.totalTime ? parseInt(newCompForm.totalTime, 10) : undefined,
      source: newCompForm.source || 'Manual Entry',
      model: newCompForm.model || undefined,
    };

    const updated = [...currentComps, newComp];
    onCompsChange(updated);

    // Reset form and close dialog
    setNewCompForm(EMPTY_COMP_FORM);
    setFormError('');
    setShowAddDialog(false);
  };

  const handlePhotoChange = useCallback((reg, newPhotoUrl) => {
    if (onPhotoChange) {
      onPhotoChange(reg, newPhotoUrl);
    }
  }, [onPhotoChange]);

  const handleReset = () => {
    if (onResetToAI) onResetToAI();
  };

  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setNewCompForm(EMPTY_COMP_FORM);
    setFormError('');
  };

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No on-market comparables data available</Typography>
      </div>
    );
  }

  const { relevanceCriteria, selectionCriteria, summary } = content;

  return (
    <div className="space-y-3 p-2 overflow-auto h-full">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Typography variant="small" className="font-semibold">
            On-Market Comparables ({currentComps.filter(c => !isExcluded(c.reg || c.registration)).length}{currentComps.some(c => isExcluded(c.reg || c.registration)) ? ` / ${currentComps.length}` : ''})
          </Typography>
          {hasLocalChanges && (
            <Chip size="sm" value="Pending" color="amber" variant="filled" />
          )}
          {hasUserEdits && !hasLocalChanges && (
            <Chip size="sm" value="Edited" color="amber" variant="ghost" />
          )}
          {isUpdating && <Spinner className="h-4 w-4" />}
        </div>

        {(onCompsChange || onResetToAI) && (
          <div className="flex items-center gap-2">
            {(hasUserEdits || hasLocalChanges || excludedListings.length > 0) && onResetToAI && (
              <Button
                variant="text"
                size="sm"
                color="gray"
                className="flex items-center gap-1 py-1 px-2"
                onClick={handleReset}
                disabled={isUpdating}
              >
                <ArrowPathIcon className="h-3 w-3" />
                Reset to AI Selection
              </Button>
            )}
            <Button
              variant="outlined"
              size="sm"
              color="blue"
              className="flex items-center gap-1 py-1 px-2"
              onClick={() => setShowAddDialog(true)}
              disabled={isUpdating}
            >
              <PlusIcon className="h-3 w-3" />
              Add Comp
            </Button>
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <Typography variant="small" color="blue-gray" className="font-medium text-xs mb-0.5">
            Selection Summary
          </Typography>
          <Typography variant="small" color="gray" className="text-xs">
            {summary}
          </Typography>
        </div>
      )}

      {/* Selection Criteria */}
      {(relevanceCriteria || selectionCriteria) && (
        <Card className="bg-gray-50">
          <CardBody className="p-2">
            <Typography variant="small" className="font-medium text-xs mb-1">
              Selection Criteria
            </Typography>
            <Typography variant="small" color="gray" className="whitespace-pre-wrap text-xs">
              {relevanceCriteria || selectionCriteria}
            </Typography>
          </CardBody>
        </Card>
      )}

      {/* Comparable Listings Grid */}
      {currentComps.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {currentComps.map((comp, index) => (
            <CompCard
              key={comp.listingId || comp.reg || comp.registration || index}
              comp={comp}
              onRemove={(() => {
                const reg = (comp.reg || comp.registration || '').toUpperCase();
                if (subjectRegistration && reg === subjectRegistration.toUpperCase()) return null;
                return (onToggleExclusion || onCompsChange) ? handleRemove : null;
              })()}
              isRemoving={isUpdating}
              isExcluded={isExcluded(comp.reg || comp.registration)}
              isSubject={subjectRegistration && (comp.reg || comp.registration || '').toUpperCase() === subjectRegistration.toUpperCase()}
              onPhotoChange={onPhotoChange ? handlePhotoChange : null}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          <Typography variant="small">No comparable listings selected</Typography>
          {onCompsChange && (
            <Button
              variant="text"
              size="sm"
              color="blue"
              className="mt-2"
              onClick={() => setShowAddDialog(true)}
            >
              Add comparables
            </Button>
          )}
        </div>
      )}

      {/* No-Price Comparables (Price on Request) */}
      {content?.noPriceComparables && content.noPriceComparables.length > 0 && (
        <>
          <Typography variant="small" className="text-gray-500 mt-4 mb-2 font-medium">
            Additional Listings (Price on Request)
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {content.noPriceComparables.map((comp, index) => (
              <NoPriceCompCard
                key={comp.id || comp.listingId || comp.reg || comp.registration || `noPrice-${index}`}
                comp={comp}
                onPhotoChange={onPhotoChange ? handlePhotoChange : null}
              />
            ))}
          </div>
        </>
      )}

      {/* Add Comp Dialog */}
      <Dialog open={showAddDialog} handler={handleCloseDialog} size="sm">
        <DialogHeader>Add Comparable</DialogHeader>
        <DialogBody>
          {formError && (
            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Registration *"
              value={newCompForm.reg}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, reg: e.target.value.toUpperCase() }))}
              placeholder="e.g., N123AB"
            />

            <Input
              label="Model"
              value={newCompForm.model}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, model: e.target.value }))}
              placeholder="e.g., Citation XLS+"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                label="Year *"
                value={newCompForm.year}
                onChange={(e) => setNewCompForm(prev => ({ ...prev, year: e.target.value }))}
                placeholder="e.g., 2019"
              />

              <Input
                type="number"
                label="Total Time (hrs)"
                value={newCompForm.totalTime}
                onChange={(e) => setNewCompForm(prev => ({ ...prev, totalTime: e.target.value }))}
                placeholder="e.g., 1200"
              />
            </div>

            <Input
              type="number"
              label="Price (USD) *"
              value={newCompForm.price_usd}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, price_usd: e.target.value }))}
              placeholder="e.g., 450000"
            />

            <Input
              label="Source"
              value={newCompForm.source}
              onChange={(e) => setNewCompForm(prev => ({ ...prev, source: e.target.value }))}
              placeholder="e.g., Controller, Trade-A-Plane"
            />
          </div>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="text" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button onClick={handleAddComp} disabled={isUpdating}>
            {isUpdating ? <Spinner className="h-4 w-4" /> : 'Add Comp'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default OnMarketCompsSection;
